/**
 * ConsultantHubAccessModal — add consultant team members to a project's Consultant Hub
 * (docs/CONSULTANT_HUB.md). Opened by the 'open-consultant-hub-access' event
 * ({ projectId, existingEmails }) from ConsultantHubSection's ⋯ menu.
 *
 * People come from the project's consultant firms (Reference › Team; awarded first, then
 * candidates) — every contact at that firm with an email — from a contact-book search, or are
 * typed by hand. Each person picks up the discipline their
 * release pages open to, guessed from the consultant's service and changeable here. Adding
 * someone emails them their own link + access code right away.
 */
import { useEffect, useState } from 'react'
import { getProjectConsultants } from '../services/projectConsultants.js'
import { getOrganizationDetail, getContactNamesByIds, getContactEmailsByIds, searchContacts } from '../services/contacts.js'
import { addHubPeople, HUB_DISCIPLINES, disciplineForService } from '../services/consultantHub.js'
import { alertDialog } from './Dialog.jsx'

const FONT = '"DM Sans", system-ui, sans-serif'
const MONO = '"DM Mono", ui-monospace, monospace'
const input = {
  fontFamily: FONT, fontSize: 13, padding: '6px 8px', borderRadius: 4,
  border: '0.5px solid var(--aj-border-mid, #b8c4c8)', background: 'var(--aj-bg-surface, #fff)', color: 'var(--aj-text, #07272D)',
}
const btn = { fontFamily: FONT, fontSize: 13, padding: '7px 14px', borderRadius: 6, border: '0.5px solid var(--aj-border-mid, #b8c4c8)', background: 'var(--aj-bg-surface, #fff)', color: 'var(--aj-text, #07272D)', cursor: 'pointer' }
const primary = { ...btn, background: '#07272D', color: '#fff', border: '0.5px solid #07272D' }
const muted = { fontFamily: FONT, fontSize: 12, color: 'var(--aj-text-muted, #7a9aaa)', lineHeight: 1.5 }

function DisciplineSelect({ value, onChange }) {
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value || null)} style={{ ...input, padding: '4px 6px', fontSize: 12 }}>
      <option value="">Opens to: all sets</option>
      {HUB_DISCIPLINES.map((d) => <option key={d.key} value={d.key}>Opens to: {d.label}</option>)}
    </select>
  )
}

export default function ConsultantHubAccessModal() {
  const [open, setOpen] = useState(false)
  const [projectId, setProjectId] = useState('')
  const [existing, setExisting] = useState(new Set())
  const [firms, setFirms] = useState(null)       // null = loading; [{ orgId, orgName, service, discipline, people:[{contactId,name,email}] }]
  const [picked, setPicked] = useState({})       // contactId -> true
  const [manual, setManual] = useState([])       // [{ name, email, orgName, discipline }]
  const [busy, setBusy] = useState(false)
  const [q, setQ] = useState('')
  const [hits, setHits] = useState(null)        // null = no search; [] / [...] = results

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      setProjectId(d.projectId || '')
      setExisting(new Set((d.existingEmails || []).map((x) => String(x).toLowerCase())))
      setFirms(null); setPicked({}); setManual([]); setBusy(false); setQ(''); setHits(null)
      setOpen(true)
    }
    window.addEventListener('open-consultant-hub-access', onOpen)
    return () => window.removeEventListener('open-consultant-hub-access', onOpen)
  }, [])

  useEffect(() => {
    if (!open || !projectId) return
    let alive = true
    ;(async () => {
      try {
        const rows = (await getProjectConsultants(projectId)).filter((r) => r.orgId)
        const byOrg = new Map()
        for (const r of rows) {
          const cur = byOrg.get(r.orgId)
          if (cur) { cur.services.push(r.consultantService); if (r.status === 'Awarded') cur.awarded = true }
          else byOrg.set(r.orgId, { orgId: r.orgId, services: [r.consultantService], awarded: r.status === 'Awarded' })
        }
        const out = []
        for (const f of byOrg.values()) {
          const org = await getOrganizationDetail(f.orgId).catch(() => null)
          const ids = (org?.fields?.['Contacts']) || []
          const [names, emails] = await Promise.all([getContactNamesByIds(ids), getContactEmailsByIds(ids)])
          out.push({
            orgId: f.orgId,
            orgName: org?.fields?.['Organization Name'] || 'Consultant',
            service: f.services.filter(Boolean).join(', '),
            discipline: f.services.map(disciplineForService).find(Boolean) || null,
            awarded: f.awarded,
            people: ids.map((id) => ({ contactId: id, name: names[id] || 'Unnamed contact', email: emails[id] || '' }))
              .sort((a, b) => a.name.localeCompare(b.name)),
          })
        }
        out.sort((a, b) => (b.awarded - a.awarded) || a.orgName.localeCompare(b.orgName))
        if (alive) setFirms(out)
      } catch {
        if (alive) setFirms([])
      }
    })()
    return () => { alive = false }
  }, [open, projectId])

  useEffect(() => {
    if (!open) return
    const term = q.trim()
    if (term.length < 2) { setHits(null); return }
    let alive = true
    const t = setTimeout(() => {
      searchContacts(term).then((r) => {
        if (!alive) return
        setHits((r?.contacts || []).map((c) => ({
          contactId: c.id,
          name: c.fields?.['Name'] || [c.fields?.['First Name'], c.fields?.['Last Name']].filter(Boolean).join(' ') || 'Unnamed contact',
          email: c.fields?.['Email'] || '',
          title: c.fields?.['Job Title'] || '',
        })).slice(0, 12))
      }).catch(() => { if (alive) setHits([]) })
    }, 300)
    return () => { alive = false; clearTimeout(t) }
  }, [q, open])

  if (!open) return null
  const close = () => { if (!busy) setOpen(false) }

  const setFirmDiscipline = (orgId, d) => setFirms((fs) => fs.map((f) => (f.orgId === orgId ? { ...f, discipline: d } : f)))
  const chosen = [
    ...(firms || []).flatMap((f) => f.people.filter((p) => picked[p.contactId]).map((p) => ({
      name: p.name, email: p.email, contactId: p.contactId, orgId: f.orgId, orgName: f.orgName, discipline: f.discipline,
    }))),
    ...manual.filter((m) => m.name.trim() && /@/.test(m.email)).map((m) => ({
      name: m.name.trim(), email: m.email.trim(), contactId: m.contactId || null, orgName: m.orgName.trim() || null, discipline: m.discipline,
    })),
  ]

  async function submit() {
    if (!chosen.length) return
    setBusy(true)
    try {
      const { added, skipped } = await addHubPeople(projectId, chosen)
      window.dispatchEvent(new Event('consultant-hub-updated'))
      setOpen(false)
      const notEmailed = (added || []).filter((a) => !a.emailed)
      const lines = []
      lines.push(`Added ${added.length} ${added.length === 1 ? 'person' : 'people'}. Each was emailed their own link and access code.`)
      if (notEmailed.length) lines.push(`The email did not go out to ${notEmailed.map((a) => a.name).join(', ')}. Use "Resend access email" on their row.`)
      if (skipped?.length) lines.push(`Skipped: ${skipped.map((s) => `${s.name} (${s.reason})`).join(', ')}.`)
      await alertDialog({ title: 'Consultant Hub', message: lines.join('\n\n') })
    } catch (e) {
      setBusy(false)
      await alertDialog({ title: 'Could not add people', message: e.message || 'Something went wrong.' })
    }
  }

  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) close() }}
      style={{ position: 'fixed', inset: 0, zIndex: 950, background: 'rgba(7,39,45,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 620, maxWidth: '95vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column', background: 'var(--aj-bg-surface, #fff)', borderRadius: 10, border: '0.5px solid var(--aj-border, #ccc)', boxShadow: '0 12px 48px rgba(0,0,0,0.35)' }}>
        <div style={{ padding: '12px 16px', background: '#07272D', color: '#fff', fontFamily: '"Montserrat", sans-serif', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', borderRadius: '10px 10px 0 0', display: 'flex' }}>
          <span style={{ flex: 1 }}>Add people to the Consultant Hub</span>
          <button onClick={close} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 14 }}>✕</button>
        </div>
        <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
          <div style={{ ...muted, marginBottom: 12 }}>
            Each person gets their own link and 6-digit access code by email, good for the whole project.
            They see every background release and are emailed when a new one goes out.
          </div>

          {firms === null && <div style={muted}>Loading the project's consultants…</div>}
          {firms && !firms.length && (
            <div style={{ ...muted, marginBottom: 12 }}>
              No consultant firms are on this project's team yet. Find people in the contact book below, or add them by hand.
            </div>
          )}
          {(firms || []).map((f) => (
            <div key={f.orgId} style={{ border: '0.5px solid var(--aj-border, #ddd)', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, color: 'var(--aj-text, #07272D)', flex: 1 }}>
                  {f.orgName}{f.service ? <span style={{ fontWeight: 400, color: 'var(--aj-text-muted, #7a9aaa)' }}> · {f.service}</span> : null}
                  {!f.awarded && <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--aj-text-muted, #7a9aaa)', marginLeft: 6 }}>(not awarded)</span>}
                </span>
                <DisciplineSelect value={f.discipline} onChange={(d) => setFirmDiscipline(f.orgId, d)} />
              </div>
              {!f.people.length && <div style={muted}>No contacts on file for this firm.</div>}
              {f.people.map((p) => {
                const already = p.email && existing.has(p.email.toLowerCase())
                const disabled = !p.email || already
                return (
                  <label key={p.contactId} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1 }}>
                    <input type="checkbox" disabled={disabled} checked={!!picked[p.contactId]}
                      onChange={(e) => setPicked((s) => ({ ...s, [p.contactId]: e.target.checked }))} />
                    <span style={{ fontFamily: FONT, fontSize: 13, color: 'var(--aj-text, #07272D)' }}>{p.name}</span>
                    <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--aj-text-muted, #7a9aaa)' }}>
                      {already ? 'already on the Hub' : (p.email || 'no email on file')}
                    </span>
                  </label>
                )
              })}
            </div>
          ))}

          <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: 'var(--aj-text, #07272D)', margin: '14px 0 6px' }}>Find someone in the contact book</div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" style={{ ...input, width: '100%', boxSizing: 'border-box', marginBottom: 6 }} />
          {hits && hits.length === 0 && <div style={{ ...muted, marginBottom: 6 }}>No contacts match.</div>}
          {(hits || []).map((h) => {
            const already = h.email && existing.has(h.email.toLowerCase())
            const queued = manual.some((m) => m.contactId === h.contactId)
            return (
              <div key={h.contactId} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}>
                <span style={{ fontFamily: FONT, fontSize: 13, color: 'var(--aj-text, #07272D)' }}>{h.name}</span>
                <span style={{ fontFamily: MONO, fontSize: 11, color: 'var(--aj-text-muted, #7a9aaa)', flex: 1 }}>
                  {already ? 'already on the Hub' : (h.email || 'no email on file')}{h.title ? ' · ' + h.title : ''}
                </span>
                <button style={{ ...btn, padding: '2px 8px', fontSize: 12 }} disabled={!h.email || already || queued}
                  onClick={() => setManual((a) => [...a, { name: h.name, email: h.email, contactId: h.contactId, orgName: '', discipline: null }])}>
                  {queued ? 'Added below' : 'Add'}
                </button>
              </div>
            )
          })}

          <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: 'var(--aj-text, #07272D)', margin: '14px 0 6px' }}>People to add</div>
          {manual.map((m, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
              <input placeholder="Name" value={m.name} style={{ ...input, flex: '1 1 120px' }}
                onChange={(e) => setManual((a) => a.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
              <input placeholder="Email" value={m.email} style={{ ...input, flex: '1 1 160px' }}
                onChange={(e) => setManual((a) => a.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))} />
              <input placeholder="Firm" value={m.orgName} style={{ ...input, flex: '1 1 120px' }}
                onChange={(e) => setManual((a) => a.map((x, j) => (j === i ? { ...x, orgName: e.target.value } : x)))} />
              <DisciplineSelect value={m.discipline} onChange={(d) => setManual((a) => a.map((x, j) => (j === i ? { ...x, discipline: d } : x)))} />
              <button style={{ ...btn, padding: '4px 8px' }} onClick={() => setManual((a) => a.filter((_, j) => j !== i))}>Remove</button>
            </div>
          ))}
          <button style={{ ...btn, padding: '5px 10px', fontSize: 12 }}
            onClick={() => setManual((a) => [...a, { name: '', email: '', contactId: null, orgName: '', discipline: null }])}>+ Add a person by hand</button>
        </div>
        <div style={{ padding: '10px 16px', borderTop: '0.5px solid var(--aj-border, #ddd)', display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
          <span style={{ ...muted, flex: 1 }}>{chosen.length ? `${chosen.length} selected` : ''}</span>
          <button style={btn} onClick={close} disabled={busy}>Cancel</button>
          <button style={{ ...primary, opacity: chosen.length && !busy ? 1 : 0.5 }} disabled={!chosen.length || busy} onClick={submit}>
            {busy ? 'Adding…' : `Add and email ${chosen.length || ''}`.trim()}
          </button>
        </div>
      </div>
    </div>
  )
}
